import { useEffect, useState } from "react";
import { Loader2, Merge, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELDS = [["seller", "Vendeurs"], ["sector", "Secteurs"], ["brand", "Marques reprises"], ["model", "Modèles"]];
const NONE = "__none__";

const MergeBox = ({ field, picked, setPicked, counts, accounts, refs = [], onDone }) => {
  const [target, setTarget] = useState("");
  const [acc, setAcc] = useState(NONE);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const ref = picked.find((n) => refs.includes(n));
    setTarget((t) => (picked.includes(t) || !t ? ref || [...picked].sort((a, b) => (counts[b] || 0) - (counts[a] || 0))[0] || "" : t));
  }, [picked, counts, refs]);
  useEffect(() => { const a = (accounts || []).find((x) => x.name === target); if (a) { setAcc(a.id); } }, [target, accounts]);
  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stats/names/${field}/merge`, { sources: picked, target, reseller_id: acc === NONE ? "" : acc });
      toast.success(`${data.updated} commande(s) mises à jour`); setPicked([]); setAcc(NONE); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Regroupement impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="sticky bottom-0 bg-card border-t border-border pt-3 mt-3 flex flex-wrap items-center gap-2" data-testid="names-merge-box">
      <span className="text-xs text-muted-foreground">{picked.length} nom(s) sélectionné(s) → nom à garder :</span>
      <Input value={target} onChange={(e) => setTarget(e.target.value)} list="names-refs" placeholder="Nom du configurateur ou autre" className="h-8 w-60 text-sm" data-testid="names-target" />
      <datalist id="names-refs">{refs.map((r) => <option key={r} value={r} />)}</datalist>
      {accounts && (
        <Select value={acc} onValueChange={setAcc}>
          <SelectTrigger className="h-8 w-60 text-xs" data-testid="names-account"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Ne pas changer le compte vendeur</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button size="sm" className="gap-1" disabled={busy || picked.length < 1 || !target.trim()} onClick={merge} data-testid="names-merge-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Merge className="w-4 h-4" />} Regrouper
      </Button>
    </div>
  );
};

export const NameCleaner = ({ open, onOpenChange, onChanged }) => {
  const [field, setField] = useState("seller");
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const load = () => { setData(null); api.get(`/stats/names/${field}`).then(({ data: d }) => setData(d)).catch(() => setData({ values: [], groups: [] })); };
  useEffect(() => { if (open) { setPicked([]); load(); } }, [open, field]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = Object.fromEntries((data?.values || []).map((v) => [v.name, v.count]));
  const refs = data?.refs || [];
  const toggle = (n, on) => setPicked((p) => (on ? [...new Set([...p, n])] : p.filter((x) => x !== n)));
  const label = (n) => n || "(vide)";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="names-dialog">
        <DialogHeader>
          <DialogTitle>Nettoyer les noms</DialogTitle>
          <DialogDescription>Cochez les variantes d'un même nom, choisissez le nom à garder (les noms du configurateur sont proposés en priorité) puis cliquez sur « Regrouper ». Les commandes historiques sont modifiées.</DialogDescription>
        </DialogHeader>
        <Tabs value={field} onValueChange={setField}>
          <TabsList>{FIELDS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`names-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <>
            <h4 className="text-xs font-bold uppercase mt-2 flex items-center gap-1"><Wand2 className="w-3.5 h-3.5 text-primary" /> Noms proches proposés ({data.groups.length})</h4>
            <div className="flex flex-wrap gap-2" data-testid="names-groups">
              {data.groups.length === 0 && <span className="text-xs text-muted-foreground">Aucun nom proche détecté.</span>}
              {data.groups.map((g, i) => (
                <button key={i} type="button" onClick={() => setPicked(g)} data-testid={`names-group-${i}`}
                  className="text-left text-xs rounded-lg border border-border px-2 py-1.5 hover:border-primary hover:bg-primary/5 transition-colors">
                  {g.map((n, j) => (
                    <span key={n}>{j > 0 && " · "}{refs.includes(n) ? <b className="text-primary">{n} (configurateur{counts[n] ? ` · ${counts[n]}` : ""})</b> : `${label(n)} (${counts[n]})`}</span>
                  ))}
                </button>
              ))}
            </div>
            <h4 className="text-xs font-bold uppercase mt-4">Tous les noms ({data.values.length})</h4>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1" data-testid="names-list">
              {data.values.map((v, i) => (
                <label key={v.name} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`names-item-${i}`}>
                  <Checkbox checked={picked.includes(v.name)} onCheckedChange={(on) => toggle(v.name, !!on)} />
                  <span className="truncate">{label(v.name)}</span>{refs.includes(v.name) && <span className="text-[10px] uppercase font-bold text-primary">config.</span>}<span className="text-xs text-muted-foreground ml-auto">{v.count}</span>
                </label>
              ))}
            </div>
            <MergeBox field={field} picked={picked} setPicked={setPicked} counts={counts} accounts={data.accounts} refs={refs} onDone={() => { load(); onChanged(); }} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
